import { useState } from "react";
import { C, getFontFamily } from "../constants/colors";
import { signInWithGoogle, signInWithEmail, signUpWithEmail, getUserProfile, createUserProfile } from "../firebase";

// Google logo SVG
const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 18 18">
    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
    <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
  </svg>
);

// SIGNIN-SPINNER-APPLIED: small spinning ring shown in the Google button while
// sign-in is in flight, so the user gets feedback and doesn't tap again.
const Spinner = () => (
  <>
    <style>{`@keyframes login-spin { to { transform: rotate(360deg); } }`}</style>
    <span style={{
      width: 16, height: 16, borderRadius: "50%", flexShrink: 0,
      border: "2px solid rgba(15,110,86,0.25)", borderTopColor: C.primaryMid,
      display: "inline-block", animation: "login-spin 0.7s linear infinite",
    }} />
  </>
);

export function LoginPage({ lang, setLang, onLogin, initialMode }) {
  const ff = getFontFamily(lang);
  // TUTORIAL-MODE-APPLIED: parent (App.js) can open us in "signup" tab when the user taps
  // "Sign up free" from the end-of-tutorial modal. Defaults to "login" for direct navigation.
  const [mode, setMode] = useState(initialMode === "signup" ? "signup" : "login"); // login | signup
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  // CLASS-10-ONLY-BETA-APPLIED: default to Class 10 (only available class during beta).
  const [studentClass, setStudentClass] = useState(10);
  const [board, setBoard] = useState("CBSE");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [needsProfile, setNeedsProfile] = useState(false);
  const [googleUser, setGoogleUser] = useState(null);
  const [showSignupNudge, setShowSignupNudge] = useState(false);
  // PARENTAL-CONSENT-APPLIED: India's DPDP Act treats under-18 as children, so account
  // creation records a parental-consent acknowledgment + an optional guardian email.
  const [parentEmail, setParentEmail] = useState("");
  const [consent, setConsent] = useState(false);

  const isHi = lang === "hi";

  // Handle Google sign-in
  const handleGoogle = async () => {
    setLoading(true);
    setError("");
    try {
      const user = await signInWithGoogle();
      if (!user) { setLoading(false); return; } // redirect in progress
      const profile = await getUserProfile(user.uid);
      if (profile) {
        // Returning user — go straight to app
        onLogin({
          uid: user.uid,
          name: profile.name || user.displayName,
          email: user.email,
          studentClass: profile.studentClass,
          board: profile.board,
          lang: profile.lang || lang,
        });
      } else {
        // First-time Google user — need class/board
        setGoogleUser(user);
        setName(user.displayName || "");
        setNeedsProfile(true);
      }
    } catch (e) {
      if (e.code === "auth/popup-closed-by-user") {
        // User closed popup, not an error
      } else {
        setError(isHi ? "Google लॉगिन विफल। पुनः प्रयास करें।" : "Google sign-in failed. Please try again.");
      }
    }
    setLoading(false);
  };

  // Handle email sign-in
  const handleEmailLogin = async () => {
    if (!email || !password) {
      setError(isHi ? "ईमेल और पासवर्ड भरें।" : "Please enter email and password.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const user = await signInWithEmail(email, password);
      const profile = await getUserProfile(user.uid);
      onLogin({
        uid: user.uid,
        name: profile?.name || email.split("@")[0],
        email: user.email,
        studentClass: profile?.studentClass || 10,
        board: profile?.board || "CBSE",
        lang: profile?.lang || lang,
      });
    } catch (e) {
      if (e.code === "auth/user-not-found" || e.code === "auth/invalid-credential") {
        setError(isHi ? "खाता नहीं मिला।" : "Account not found.");
        setShowSignupNudge(true);
      } else if (e.code === "auth/wrong-password") {
        setError(isHi ? "गलत पासवर्ड।" : "Incorrect password.");
      } else {
        setError(isHi ? "लॉगिन विफल। पुनः प्रयास करें।" : "Login failed. Please try again.");
      }
    }
    setLoading(false);
  };

  // Handle email sign-up
  const handleEmailSignup = async () => {
    if (!name || !email || !password) {
      setError(isHi ? "सभी फ़ील्ड भरें।" : "Please fill all fields.");
      return;
    }
    if (password.length < 6) {
      setError(isHi ? "पासवर्ड कम से कम 6 अक्षर का होना चाहिए।" : "Password must be at least 6 characters.");
      return;
    }
    if (!consent) {
      setError(isHi ? "जारी रखने के लिए अभिभावक की सहमति की पुष्टि करें।" : "Please confirm parental consent to continue.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const user = await signUpWithEmail(email, password);
      await createUserProfile(user.uid, {
        name,
        email,
        studentClass,
        board,
        lang,
        parentEmail: parentEmail.trim(),
        parentalConsent: true,
        parentConsentAt: new Date().toISOString(),
      });
      onLogin({
        uid: user.uid,
        name,
        email,
        studentClass,
        board,
        lang,
      });
    } catch (e) {
      if (e.code === "auth/email-already-in-use") {
        setError(isHi ? "यह ईमेल पहले से पंजीकृत है। लॉगिन करें।" : "Email already registered. Please sign in.");
      } else if (e.code === "auth/invalid-email") {
        setError(isHi ? "अमान्य ईमेल।" : "Invalid email address.");
      } else {
        setError(isHi ? "साइन अप विफल। पुनः प्रयास करें।" : "Sign up failed. Please try again.");
      }
    }
    setLoading(false);
  };

  // Handle profile completion for first-time Google users
  const handleCompleteProfile = async () => {
    if (!googleUser) return;
    if (!consent) {
      setError(isHi ? "जारी रखने के लिए अभिभावक की सहमति की पुष्टि करें।" : "Please confirm parental consent to continue.");
      return;
    }
    setLoading(true);
    try {
      await createUserProfile(googleUser.uid, {
        name: name || googleUser.displayName,
        email: googleUser.email,
        studentClass,
        board,
        lang,
        parentEmail: parentEmail.trim(),
        parentalConsent: true,
        parentConsentAt: new Date().toISOString(),
      });
      onLogin({
        uid: googleUser.uid,
        name: name || googleUser.displayName,
        email: googleUser.email,
        studentClass,
        board,
        lang,
      });
    } catch (e) {
      setError(isHi ? "प्रोफ़ाइल सहेजने में विफल।" : "Failed to save profile.");
    }
    setLoading(false);
  };

  // Shared styles — LOGIN-READABILITY-APPLIED: white field cards, navy labels for stronger contrast.
  const inputStyle = {
    width: "100%", boxSizing: "border-box", padding: "12px 14px",
    borderRadius: 10, border: "1px solid #D8E0EA", fontSize: 14,
    fontFamily: ff, background: "#FFFFFF", color: "#1E2A4A",
    outline: "none",
  };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: "#1E2A4A", display: "block", marginBottom: 6 };
  const btnPrimary = {
    width: "100%", padding: "14px", borderRadius: 12, border: "none",
    background: C.primaryMid, color: "white", fontSize: 15,
    fontWeight: 600, cursor: loading ? "wait" : "pointer", fontFamily: ff,
    opacity: loading ? 0.7 : 1,
  };

  // PARENTAL-CONSENT-APPLIED: shared block (optional guardian email + required consent
  // acknowledgment) rendered on both account-creation paths — email sign-up and the
  // first-time Google profile screen. Required for under-18 users per India's DPDP Act.
  const PRIVACY_URL = "https://samajhapp.com/privacy";
  const openPrivacy = (e) => { e.preventDefault(); e.stopPropagation(); window.open(PRIVACY_URL, "_blank", "noopener,noreferrer"); };
  const renderConsentBlock = () => (
    <div style={{ marginBottom: 20 }}>
      <label style={labelStyle}>{isHi ? "अभिभावक का ईमेल (वैकल्पिक)" : "Parent / guardian email (optional)"}</label>
      <input type="email" placeholder="parent@email.com" value={parentEmail}
        onChange={e => setParentEmail(e.target.value)} style={{ ...inputStyle, marginBottom: 14 }} />
      <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
        <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)}
          style={{ marginTop: 2, width: 18, height: 18, flexShrink: 0, accentColor: C.primaryMid, cursor: "pointer" }} />
        <span style={{ fontSize: 12, color: "#5C6B82", lineHeight: 1.5 }}>
          {isHi
            ? "मैं पुष्टि करता/करती हूँ कि मेरे माता-पिता/अभिभावक इस खाते और सीखने की जानकारी एकत्र करने की सहमति देते हैं। "
            : "I confirm a parent or guardian consents to this account and to Samajh collecting learning data. "}
          <span onClick={openPrivacy} style={{ color: C.primaryMid, fontWeight: 600, textDecoration: "underline" }}>
            {isHi ? "गोपनीयता नीति" : "Privacy Policy"}
          </span>
        </span>
      </label>
    </div>
  );

  // ── Profile completion screen (first Google login) ──
  if (needsProfile) return (
    <div style={{ minHeight: "calc(100vh / 0.92)", zoom: 0.92, background: "#E8F2F8", fontFamily: ff, display: "flex", flexDirection: "column" }}>
      <div style={{ background: C.skyDark, padding: "48px 28px 36px", textAlign: "center" }}>
        <div style={{ width: 52, height: 52, borderRadius: 16, background: C.primaryMid, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
          <span style={{ color: "white", fontWeight: 600, fontSize: 22, fontFamily: "serif" }}>S</span>
        </div>
        <div style={{ fontSize: 20, fontWeight: 600, color: "white", marginBottom: 6 }}>
          {isHi ? "लगभग तैयार!" : "Almost there!"}
        </div>
        <div style={{ fontSize: 13, color: C.primaryLight }}>
          {isHi ? "अपनी कक्षा और बोर्ड बताएं" : "Tell us your class and board"}
        </div>
      </div>
      <div style={{ padding: 28, maxWidth: 400, margin: "0 auto", width: "100%" }}>
        <div style={{ background: C.surface, borderRadius: 16, padding: "24px", border: `0.5px solid ${C.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, paddingBottom: 16, borderBottom: `0.5px solid ${C.border}` }}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", background: C.primarySoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: C.primaryMid }}>{(name || "S")[0].toUpperCase()}</span>
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>{name || googleUser?.displayName}</div>
              <div style={{ fontSize: 12, color: C.textMute }}>{googleUser?.email}</div>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
            <div>
              <label style={labelStyle}>{isHi ? "कक्षा" : "Class"}</label>
              {/* CLASS-10-ONLY-BETA-APPLIED: button grid (replaces native select). Non-10 classes
                  show a lock icon + "Coming soon" so the disabled state is unmistakable. */}
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[6, 7, 8, 9, 10].map(c => {
                  const isAvailable = c === 10;
                  const isSelected = studentClass === c;
                  return (
                    <div
                      key={c}
                      onClick={() => { if (isAvailable) setStudentClass(c); }}
                      style={{
                        flex: "1 1 60px", minWidth: 60, padding: "10px 6px",
                        textAlign: "center", borderRadius: 10,
                        background: isSelected ? C.primary : (isAvailable ? "#FFFFFF" : "#F5F2EC"),
                        color: isSelected ? "#FFFFFF" : (isAvailable ? C.text : "#9A8870"),
                        border: isSelected ? `2px solid ${C.primary}` : `1.5px solid ${C.border}`,
                        cursor: isAvailable ? "pointer" : "not-allowed",
                        opacity: isAvailable ? 1 : 0.6,
                        fontWeight: 600, fontSize: 13, lineHeight: 1.2,
                        transition: "all 0.15s",
                        display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
                      }}
                    >
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        {!isAvailable && (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                          </svg>
                        )}
                        <span>{isHi ? `कक्षा ${c}` : `Class ${c}`}</span>
                      </div>
                      {!isAvailable && (
                        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase" }}>
                          {isHi ? "जल्द" : "Soon"}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <label style={labelStyle}>{isHi ? "बोर्ड" : "Board"}</label>
              <select value={board} onChange={e => setBoard(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
                <option value="CBSE">CBSE</option>
                <option value="ICSE">ICSE</option>
              </select>
            </div>
          </div>
          {renderConsentBlock()}
          {error && <div style={{ color: "#E24B4A", fontSize: 13, marginBottom: 12, textAlign: "center" }}>{error}</div>}
          <button onClick={handleCompleteProfile} disabled={loading} style={btnPrimary}>
            {loading
              ? (isHi ? "सहेजा जा रहा है..." : "Saving...")
              : (isHi ? "शुरू करें →" : "Start learning →")}
          </button>
        </div>
      </div>
    </div>
  );

  // ── Login screen ──
  if (mode === "login") return (
    <div style={{ minHeight: "calc(100vh / 0.92)", zoom: 0.92, background: "#E8F2F8", fontFamily: ff, display: "flex", flexDirection: "column" }}>
      {/* LOGIN-READABILITY-APPLIED: header padding bumped (+8 top/bottom). Title navy for contrast on light-sky bg
          (was "white" — broken since C.skyDark token never resolved). Subtitle slate per readability spec. */}
      <div style={{ padding: "56px 28px 44px", textAlign: "center" }}>
        <div style={{ width: 52, height: 52, borderRadius: 16, background: C.primaryMid, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
          <span style={{ color: "white", fontWeight: 600, fontSize: 22, fontFamily: "serif" }}>S</span>
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: "#1E2A4A", marginBottom: 8 }}>
          {isHi ? "Samajh में आपका स्वागत है" : "Welcome to Samajh"}
        </div>
        <div style={{ fontSize: 13, color: "#5C6B82", lineHeight: 1.5 }}>
          {isHi ? "लॉगिन करें — अपनी प्रगति सहेजें" : "Sign in to save your progress and pick up where you left off"}
        </div>
      </div>

      <div style={{ padding: "0 28px 28px", maxWidth: 400, margin: "0 auto", width: "100%" }}>
        {/* Google sign-in — +8px bottom for major-section gap */}
        <button onClick={handleGoogle} disabled={loading} style={{
          width: "100%", padding: "14px", borderRadius: 12,
          border: `0.5px solid ${C.border}`, background: "#FFFFFF",
          display: "flex", alignItems: "center", justifyContent: "center",
          gap: 10, cursor: loading ? "wait" : "pointer", fontFamily: ff, marginBottom: 20,
        }}>
          {loading ? (
            <>
              <Spinner />
              <span style={{ fontSize: 15, fontWeight: 600, color: C.text }}>
                {isHi ? "साइन इन हो रहा है..." : "Signing in..."}
              </span>
            </>
          ) : (
            <>
              <GoogleLogo />
              <span style={{ fontSize: 15, fontWeight: 600, color: C.text }}>
                {isHi ? "Google से जारी रखें" : "Continue with Google"}
              </span>
            </>
          )}
        </button>

        {/* Divider — +8px each side; text color slate per readability spec */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "28px 0" }}>
          <div style={{ flex: 1, height: "0.5px", background: C.border }} />
          <span style={{ fontSize: 12, color: "#5C6B82", fontWeight: 500 }}>
            {isHi ? "या ईमेल से" : "or sign in with email"}
          </span>
          <div style={{ flex: 1, height: "0.5px", background: C.border }} />
        </div>

        {/* Email */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>{isHi ? "ईमेल" : "Email"}</label>
          <input type="email" placeholder="your@email.com" value={email}
            onChange={e => setEmail(e.target.value)} style={inputStyle}
            onKeyDown={e => e.key === "Enter" && document.getElementById("pwd")?.focus()} />
        </div>

        {/* Password — +8px below to widen gap before the action button */}
        <div style={{ marginBottom: 28 }}>
          <label style={labelStyle}>{isHi ? "पासवर्ड" : "Password"}</label>
          <input id="pwd" type="password" placeholder={isHi ? "पासवर्ड दर्ज करें" : "Enter password"} value={password}
            onChange={e => setPassword(e.target.value)} style={inputStyle}
            onKeyDown={e => e.key === "Enter" && handleEmailLogin()} />
        </div>

        {/* Error + Signup nudge */}
        {error && <div style={{ color: "#E24B4A", fontSize: 13, marginBottom: showSignupNudge ? 8 : 12, textAlign: "center" }}>{error}</div>}
        {showSignupNudge && (
          <div onClick={() => { setMode("signup"); setError(""); setShowSignupNudge(false); }}
            style={{ background: C.primarySoft, borderRadius: 12, padding: "12px 16px", marginBottom: 14, cursor: "pointer", textAlign: "center" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.primaryMid }}>
              {isHi ? "अभी खाता बनाएं →" : "Create an account →"}
            </div>
            <div style={{ fontSize: 12, color: C.primaryMuted, marginTop: 2 }}>
              {isHi ? "सिर्फ 30 सेकंड लगेंगे" : "It only takes 30 seconds"}
            </div>
          </div>
        )}

        {/* Sign in button */}
        <button onClick={handleEmailLogin} disabled={loading} style={btnPrimary}>
          {loading ? (isHi ? "लॉगिन हो रहा है..." : "Signing in...") : (isHi ? "लॉगिन करें" : "Sign in")}
        </button>

        {/* Toggle to signup */}
        <div style={{ textAlign: "center", marginTop: 16, fontSize: 13, color: C.textMid }}>
          {isHi ? "खाता नहीं है? " : "Don't have an account? "}
          <span onClick={() => { setMode("signup"); setError(""); }} style={{ color: C.primaryMid, fontWeight: 600, cursor: "pointer" }}>
            {isHi ? "साइन अप करें" : "Sign up"}
          </span>
        </div>

        {/* Footer — LOGIN-READABILITY-APPLIED: +8px gap above; text color slate. */}
        <div style={{ textAlign: "center", marginTop: 36, paddingTop: 28, borderTop: `0.5px solid ${C.border}` }}>
          <div style={{ fontSize: 11, color: "#5C6B82", lineHeight: 1.6, fontWeight: 500 }}>
            {isHi
              ? "लॉगिन करके आप हमारी सेवा की शर्तों से सहमत हैं।"
              : "By signing in you agree to our Terms of Service."}
            <br />
            {isHi
              ? "आपकी प्रगति सुरक्षित रूप से सहेजी जाती है।"
              : "Your progress and learning data is saved securely."}
          </div>
        </div>
      </div>
    </div>
  );

  // ── Signup screen ──
  // LOGIN-READABILITY-APPLIED: same readability fixes as login screen — title navy for contrast,
  // subtitle/divider slate, +8px between major sections.
  return (
    <div style={{ minHeight: "calc(100vh / 0.92)", zoom: 0.92, background: "#E8F2F8", fontFamily: ff, display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "48px 28px 36px", textAlign: "center" }}>
        <div style={{ width: 48, height: 48, borderRadius: 14, background: C.primaryMid, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
          <span style={{ color: "white", fontWeight: 600, fontSize: 20, fontFamily: "serif" }}>S</span>
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#1E2A4A", marginBottom: 6 }}>
          {isHi ? "अपना खाता बनाएं" : "Create your account"}
        </div>
        <div style={{ fontSize: 13, color: "#5C6B82", lineHeight: 1.5 }}>
          {isHi ? "Samajh के साथ सीखना शुरू करें" : "Start your learning journey with Samajh"}
        </div>
      </div>

      <div style={{ padding: "0 28px 28px", maxWidth: 400, margin: "0 auto", width: "100%" }}>
        {/* Google sign-up */}
        <button onClick={handleGoogle} disabled={loading} style={{
          width: "100%", padding: "14px", borderRadius: 12,
          border: `0.5px solid ${C.border}`, background: "#FFFFFF",
          display: "flex", alignItems: "center", justifyContent: "center",
          gap: 10, cursor: loading ? "wait" : "pointer", fontFamily: ff, marginBottom: 20,
        }}>
          {loading ? (
            <>
              <Spinner />
              <span style={{ fontSize: 15, fontWeight: 600, color: C.text }}>
                {isHi ? "साइन इन हो रहा है..." : "Signing in..."}
              </span>
            </>
          ) : (
            <>
              <GoogleLogo />
              <span style={{ fontSize: 15, fontWeight: 600, color: C.text }}>
                {isHi ? "Google से जारी रखें" : "Continue with Google"}
              </span>
            </>
          )}
        </button>

        {/* Divider — slate text + extra spacing */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "24px 0" }}>
          <div style={{ flex: 1, height: "0.5px", background: C.border }} />
          <span style={{ fontSize: 12, color: "#5C6B82", fontWeight: 500 }}>
            {isHi ? "या ईमेल से बनाएं" : "or create with email"}
          </span>
          <div style={{ flex: 1, height: "0.5px", background: C.border }} />
        </div>

        {/* Name */}
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>{isHi ? "पूरा नाम" : "Full name"}</label>
          <input type="text" placeholder={isHi ? "आपका नाम" : "Your name"} value={name}
            onChange={e => setName(e.target.value)} style={inputStyle} />
        </div>

        {/* Email */}
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>{isHi ? "ईमेल" : "Email"}</label>
          <input type="email" placeholder="your@email.com" value={email}
            onChange={e => setEmail(e.target.value)} style={inputStyle} />
        </div>

        {/* Password */}
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>{isHi ? "पासवर्ड" : "Password"}</label>
          <input type="password" placeholder={isHi ? "कम से कम 6 अक्षर" : "Min 6 characters"} value={password}
            onChange={e => setPassword(e.target.value)} style={inputStyle} />
        </div>

        {/* Class + Board */}
        <div style={{ marginBottom: 24 }}>
          <label style={labelStyle}>{isHi ? "कक्षा" : "Class"}</label>
          {/* CLASS-10-ONLY-BETA-APPLIED: button grid (replaces native select) so we can
              visibly disable non-10 classes with "Coming soon" + cursor not-allowed.
              Same shape as the profile-completion screen earlier in this file. */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[6, 7, 8, 9, 10].map(c => {
              const isAvailable = c === 10;
              const isSelected = studentClass === c;
              return (
                <div
                  key={c}
                  onClick={() => { if (isAvailable) setStudentClass(c); }}
                  style={{
                    flex: "1 1 60px", minWidth: 60, padding: "10px 6px",
                    textAlign: "center", borderRadius: 10,
                    background: isSelected ? C.primary : (isAvailable ? "#FFFFFF" : "#F5F2EC"),
                    color: isSelected ? "#FFFFFF" : (isAvailable ? C.text : "#9A8870"),
                    border: isSelected ? `2px solid ${C.primary}` : `1.5px solid ${C.border}`,
                    cursor: isAvailable ? "pointer" : "not-allowed",
                    opacity: isAvailable ? 1 : 0.6,
                    fontWeight: 600, fontSize: 13, lineHeight: 1.2,
                    transition: "all 0.15s",
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
                  }}
                >
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                    {!isAvailable && (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                    )}
                    <span>{isHi ? `कक्षा ${c}` : `Class ${c}`}</span>
                  </div>
                  {!isAvailable && (
                    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase" }}>
                      {isHi ? "जल्द" : "Soon"}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div style={{ marginBottom: 24 }}>
          <label style={labelStyle}>{isHi ? "बोर्ड" : "Board"}</label>
          <select value={board} onChange={e => setBoard(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
            <option value="CBSE">CBSE</option>
            <option value="ICSE">ICSE</option>
          </select>
        </div>

        {renderConsentBlock()}

        {/* Error */}
        {error && <div style={{ color: "#E24B4A", fontSize: 13, marginBottom: 12, textAlign: "center" }}>{error}</div>}

        {/* Create button */}
        <button onClick={handleEmailSignup} disabled={loading} style={btnPrimary}>
          {loading ? (isHi ? "खाता बन रहा है..." : "Creating account...") : (isHi ? "खाता बनाएं" : "Create account")}
        </button>

        {/* Toggle to login */}
        <div style={{ textAlign: "center", marginTop: 14, fontSize: 13, color: C.textMid }}>
          {isHi ? "पहले से खाता है? " : "Already have an account? "}
          <span onClick={() => { setMode("login"); setError(""); }} style={{ color: C.primaryMid, fontWeight: 600, cursor: "pointer" }}>
            {isHi ? "लॉगिन करें" : "Sign in"}
          </span>
        </div>
      </div>
    </div>
  );
}
